import { useI18n, translate as t } from "../i18n";
import { AnimatePresence, motion } from "motion/react";
import { ChevronUp } from "lucide-react";
import { useLenis } from "./SmoothScroll";
import { useScrolledPast } from "../hooks/useScrolledPast";
import { useDarkBackgroundBehindDockCorner } from "../hooks/useDarkBackgroundBehindPoint";

const SHOW_AFTER = 400;

export default function BackToTop() {
  const { t, language } = useI18n("BackToTop");
  const lenis = useLenis();
  const visible = useScrolledPast(SHOW_AFTER);
  const darkBehind = useDarkBackgroundBehindDockCorner(visible);

  const shell = darkBehind
    ? "border-gold/70 bg-[#020704]/95 text-gold shadow-[0_14px_36px_rgba(0,0,0,0.22),0_0_22px_rgba(241,200,106,0.18)] hover:border-brand/80 hover:text-brand focus-visible:ring-brand/50"
    : "border-gold/45 bg-[#020704]/92 text-gold shadow-[0_18px_44px_rgba(0,0,0,0.42),0_0_24px_rgba(66,255,117,0.1)] hover:border-brand/80 hover:text-brand focus-visible:ring-brand/50";

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          key="back-to-top"
          data-back-to-top="true"
          initial={{ opacity: 0, scale: 0.85, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={`large-dock-button group fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center overflow-hidden border backdrop-blur-md transition-[opacity,transform,background-color,color,border-color,box-shadow] hover:-translate-y-0.5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${shell}`}
          style={{
            clipPath:
              "polygon(10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px), 0 10px)",
          }}
          aria-label={t("Back to top")}
          onClick={() => {
            if (lenis) lenis.scrollTo(0);
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(241,200,106,0.16),transparent_45%,rgba(66,255,117,0.12))]" />
          <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 skew-x-[-18deg] bg-white/16 opacity-0 blur-[1px] transition-all duration-700 group-hover:left-[125%] group-hover:opacity-100" />
          <span className="pointer-events-none absolute inset-x-2 top-1.5 h-px bg-gradient-to-r from-transparent via-gold/70 to-transparent transition-colors group-hover:via-brand/80" />
          <ChevronUp className="large-dock-button__icon relative h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={2.6} aria-hidden />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
